import { useState } from "react";
import { useEditImageMutation, useRegisterAssetMutation } from "../redux/api";
import type { ImageEdit } from "../utils/imageEdit";
import { toEditInput, hasAnyChange } from "../utils/imageEdit";

export interface EditedImage {
  key: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  /**
   * Presigned url for the rendered file, so a caller can show it straight
   * away instead of waiting for whatever record it attaches to to round-trip.
   * Comes from the gallery registration below, so it is absent on the rare
   * path where that fails — callers must cope with not having it.
   */
  url?: string | null;
}

// Sibling to useImageUpload for images that are already in S3: instead of
// sending bytes, it sends the *description* of an edit and the server renders
// it (see services/src/common/aws/s3/image-edit.ts). Nothing is uploaded and
// nothing is read back into the browser, which is why this works identically
// on a photo picked two seconds ago and one uploaded last year — and why it
// needs no CORS access to the private bucket.
//
// The result is a new object, registered in the Gallery like any other upload;
// the caller decides what to point at it.
export const useImageEdit = () => {
  const [editImage] = useEditImageMutation();
  const [registerAsset] = useRegisterAssetMutation();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyEdit = async (
    key: string,
    fileName: string,
    edit: ImageEdit,
  ): Promise<EditedImage | null> => {
    setError(null);

    // Saving an untouched image would just add a duplicate to the Gallery.
    if (!hasAnyChange(edit)) {
      setError("Nothing to apply — adjust the image first.");
      return null;
    }

    setEditing(true);
    try {
      const result = await editImage({ key, fileName, edit: toEditInput(edit) }).unwrap();

      // Best-effort, same reasoning as useImageUpload — the render already
      // succeeded and the caller is about to point a record at it, so a
      // failure here shouldn't fail the edit; it just means this file won't
      // show up in the Gallery listing (and the caller waits for its own
      // refetch to get a url).
      let url: string | null | undefined;
      try {
        url = (
          await registerAsset({
            key: result.key,
            fileName: result.fileName,
            mimeType: result.mimeType,
            fileSize: result.fileSize,
          }).unwrap()
        ).url;
      } catch {
        // Swallow — see comment above.
      }

      return { ...result, url };
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to apply the edit.");
      return null;
    } finally {
      setEditing(false);
    }
  };

  return { applyEdit, editing, error };
};
